/**
 * Constantes métier liées à la livraison et au transport.
 * Source unique — à modifier ici pour mettre à jour toute l'application.
 */

/**
 * Villes desservies. Une seule pour l'instant : la liste existe pour que
 * l'ouverture à une deuxième ne demande pas de retoucher les écrans.
 */
export const DELIVERY_CITIES = [
  { value: "libreville", label: "Libreville" },
];

export const DEFAULT_DELIVERY_CITY = DELIVERY_CITIES[0].value;

/** Zones de livraison de la ville, et le prix de la course. */
export const DELIVERY_ZONES = [
  { value: "libreville", label: "Libreville", price: 1500 },
  { value: "akanda",     label: "Akanda",     price: 2000 },
  { value: "owendo",     label: "Owendo",     price: 2000 },
];

/** Map { value → price } pour accès direct par clé de zone */
export const DELIVERY_FEES = Object.fromEntries(
  DELIVERY_ZONES.map((z) => [z.value, z.price])
);

export const SHIPPING_MODES = [
  { value: "fret aérien",   label: "Avion Express" },
  { value: "fret maritime", label: "Bateau" },
];

export const TRANSPORT_MODES = [
  { key: "aerien",   label: "Avion Express", delay: "5 à 7 jours A/D",  delayDays: [5, 7],    pricePerKg: 7500 },
  { key: "maritime", label: "Bateau",        delay: "60 - 90 jours",  delayDays: [60, 90],  pricePerKg: 3500 },
];

/** Valeur API du mode de transport → clé interne utilisée par TRANSPORT_MODES. */
export const SHIPPING_MODE_KEYS = {
  "fret aérien": "aerien",
  "fret maritime": "maritime",
};

/**
 * Mode conseillé par l'administration (`products.default_shipping_mode`).
 *
 * Renvoie `null` si aucun défaut n'est fixé ou si le défaut ne fait pas partie des
 * modes réellement proposés : le repli maritime est un choix technique, pas une
 * recommandation, et ne doit donc jamais être présenté comme telle au client.
 */
export const resolveRecommendedShippingMode = (availableModes, defaultMode) => {
  if (!defaultMode) return null;
  const modes = Array.isArray(availableModes) ? availableModes : [];
  return modes.includes(defaultMode) ? defaultMode : null;
};

/**
 * Mode présélectionné à l'ouverture : le mode conseillé s'il existe, sinon le
 * bateau, à défaut l'avion. Contrairement à la recommandation, renvoie toujours
 * une valeur exploitable.
 */
export const resolvePreferredShippingMode = (availableModes, defaultMode) => {
  const modes = Array.isArray(availableModes) && availableModes.length > 0
    ? availableModes
    : ["fret aérien", "fret maritime"];
  return (
    resolveRecommendedShippingMode(modes, defaultMode) ||
    (modes.includes("fret maritime") ? "fret maritime" : "fret aérien")
  );
};

/** Taux import aérien utilisé pour le calcul de rentabilité (CoutLivraison) */
export const SHIPPING_RATE_PER_KG = 7500;

/**
 * Tarifs transport par défaut (fallback si l'API ne renvoie pas les settings).
 * Alignés sur les settings backend `amount_per_kg` / `amount_per_cbm` : un écart
 * ici afficherait une estimation fausse au checkout.
 */
export const DEFAULT_AIR_RATE_PER_KG = 11500;   // Avion express — F CFA / kg
export const DEFAULT_SEA_RATE_PER_CBM = 210000; // Bateau — F CFA / CBM (m³)

/**
 * Taux de repli, utilisés uniquement si `GET /pricing` ne répond pas.
 * La source de vérité est le backend : ces valeurs ne doivent jamais servir à
 * annoncer un montant durablement différent de celui que l'API facture.
 *
 * Les deux couvrent ensemble les 9 % de frais bancaires : 7 % déjà absorbés
 * dans le prix de vente du produit, 2 % de frais d'import prélevés au checkout.
 */
export const DEFAULT_IMPORT_FEES_RATE = 0.02;
export const DEFAULT_BANKING_FEES_RATE = 0.07;

/**
 * Plafond des frais d'import sur les petites commandes : sous ce seuil de
 * sous-total, le frais ne dépasse jamais ce montant, quel que soit le taux.
 * Vient du backend (`GET /pricing`, `import_fees_cap_threshold` /
 * `import_fees_cap_amount`, réglables en paramètres) ; repli le temps du
 * premier appel.
 */
export const DEFAULT_IMPORT_FEES_CAP_THRESHOLD = 25_000;
export const DEFAULT_IMPORT_FEES_CAP_AMOUNT = 500;

/**
 * Assurance de la marchandise : prélèvement distinct des frais bancaires,
 * assis sur le seul sous-total. Il ne bouge ni avec le transport estimé, ni
 * avec la validation ou la correction du transport final.
 *
 * Montant fixe par palier de sous-total, plafonné à 5 000 FCFA au-delà de
 * 400 000 FCFA — jamais un taux. Le barème vient du backend (`GET /pricing`,
 * clé `insurance_tiers`, réglable en paramètres) ; celui-ci n'est qu'un repli
 * le temps du premier appel. `max: null` fait plafond, toujours en dernier.
 */
export const DEFAULT_INSURANCE_TIERS = [
  { max: 25_000, amount: 500 },
  { max: 50_000, amount: 1_000 },
  { max: 100_000, amount: 1_500 },
  { max: 200_000, amount: 2_000 },
  { max: 300_000, amount: 3_000 },
  { max: 400_000, amount: 4_000 },
  { max: null, amount: 5_000 },
];

/** Pas de facturation du poids aérien : demi-kilo supérieur */
export const AIR_MEASURE_STEP = 0.5;

/** Ratio transport à payer à la commande — Avion : 80% maintenant, 20% à réception */
export const AVION_PAYMENT_RATIO = 0.80;

/** Ratio transport à payer à la commande — Bateau : 0% (tout à l'arrivée) */
export const BATEAU_PAYMENT_RATIO = 0;

/** @deprecated Conservés pour compat — le bateau est désormais 0% à la commande */
export const BATEAU_PAYMENT_RATIO_HIGH = 0;
export const BATEAU_PAYMENT_RATIO_LOW  = 0;

export const PAYMENT_METHODS = ["Airtel Money", "En espèces"];

/**
 * Miroir de l'enum backend `OrderStatus` — statut **interne**, destiné à
 * l'équipe. Les valeurs sont les chaînes exactes renvoyées par l'API : toute
 * divergence casse silencieusement les comparaisons.
 *
 * AVAILABLE → RELEASED (retrait à l'agence) ou DELIVERED (livraison à l'adresse),
 * RETURNED pour une restitution.
 *
 * L'application cliente n'affiche pas ces valeurs : elle lit `customer_status`
 * ({@link CUSTOMER_ORDER_STATUSES}), qui masque les étapes de gestion.
 */
export const ORDER_STATUSES = {
  PENDING:   "En attente de paiement",
  AWAITING_PAYMENT: "Paiement en cours",
  PAID:      "Payé",
  AVAILABLE: "Disponible",
  RELEASED:  "Sortie",
  DELIVERED: "Livré",
  RETURNED:  "Rendu",
  CANCELLED: "Annulé",
};

/**
 * Miroir de l'enum backend `CustomerOrderStatus` — le suivi tel que le client le
 * voit, renvoyé dans `customer_status`. Deux parcours selon l'origine de la
 * marchandise :
 *
 * - import : PENDING → PAID → PROCESSING → WAREHOUSE → SHIPPED → AVAILABLE → DELIVERED
 * - local  : PENDING → PAID → DELIVERED
 *
 * Le backend est seul à décider de l'étape : PROCESSING et SHIPPED se déduisent
 * de dates, ce calcul ne doit surtout pas être refait ici.
 */
export const CUSTOMER_ORDER_STATUSES = {
  PENDING:    "En attente",
  // Distinct de ORDER_STATUSES.PENDING ("En attente de paiement", interne) :
  // même libellé pour deux états différents portait à confusion côté client.
  AWAITING_PAYMENT: "Paiement en attente",
  PAID:       "Payé",
  PROCESSING: "Traitement",
  WAREHOUSE:  "À l'entrepôt",
  SHIPPED:    "Expédiée",
  AVAILABLE:  "Disponible",
  DELIVERED:  "Livré",
  CANCELLED:  "Annulée",
};

/**
 * Style du badge de statut d'une commande, indexé par la valeur exacte de l'API.
 * La table couvre les deux enums : le Dashboard affiche le statut interne,
 * l'application cliente le statut client, et un même libellé (« Payé »,
 * « Disponible », « Livré ») vaut pour les deux.
 *
 * Le repli `default` est ambré, ce qui se lit comme « en attente » : tout statut
 * absent de cette table serait donc affiché à tort comme non traité. Toute
 * nouvelle valeur des deux enums doit y être ajoutée.
 */
export const ORDER_STATUS_STYLES = {
  // Mobile money démarré, webhook du fournisseur pas encore reçu — distinct
  // du repli ambré « en attente » : ici un paiement est réellement en cours.
  [ORDER_STATUSES.AWAITING_PAYMENT]:    "bg-cyan-50 text-cyan-600 border-cyan-100",
  [CUSTOMER_ORDER_STATUSES.AWAITING_PAYMENT]: "bg-cyan-50 text-cyan-600 border-cyan-100",
  [ORDER_STATUSES.PAID]:               "bg-green-50 text-green-600 border-green-100",
  [ORDER_STATUSES.AVAILABLE]:          "bg-teal-50 text-teal-600 border-teal-100",
  [ORDER_STATUSES.RELEASED]:           "bg-indigo-50 text-indigo-600 border-indigo-100",
  [ORDER_STATUSES.DELIVERED]:          "bg-blue-50 text-blue-600 border-blue-100",
  [ORDER_STATUSES.RETURNED]:           "bg-amber-50 text-amber-700 border-amber-100",
  [ORDER_STATUSES.CANCELLED]:          "bg-red-50 text-red-600 border-red-100",
  [CUSTOMER_ORDER_STATUSES.PROCESSING]: "bg-orange-50 text-orange-600 border-orange-100",
  [CUSTOMER_ORDER_STATUSES.WAREHOUSE]:  "bg-violet-50 text-violet-600 border-violet-100",
  [CUSTOMER_ORDER_STATUSES.SHIPPED]:    "bg-sky-50 text-sky-600 border-sky-100",
  [CUSTOMER_ORDER_STATUSES.CANCELLED]:  "bg-red-50 text-red-600 border-red-100",
  // Statut dérivé, pas rendu par l'API : une commande dont le solde de transport
  // reste dû, pour éviter « Payé » à côté d'un bouton « Payer le transport ».
  "Transport à régler":                 "bg-orange-50 text-orange-600 border-orange-100",
  default: "bg-amber-50 text-amber-700 border-amber-100",
};
